import React from 'react';
import {
    Alert,
    FormControl,
    Grid2,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useGetShow } from '@openapi/shows/shows';
import { useGetUsers } from '@openapi/users/users';
import useNotify from '../../../hooks/useNotify';

function ExpediaForm({ values, setFieldValue }) {
    const { t } = useTranslation();
    const { notifyError } = useNotify();

    const { data: showData } = useGetShow(Number(values.showId), {
        query: { enabled: !!values.showId },
    });
    const venueId = showData?.venueId;

    // showId is required so the backend populates each user's commissionGroupNames.
    const { data: usersData } = useGetUsers(
        venueId,
        { pageSize: 1000, showId: values.showId },
        {
            query: {
                enabled: !!venueId && !!values.showId,
                onError: notifyError,
            },
        },
    );
    const agents = usersData?.users || [];

    return (
        <>
            <Grid2 size={{ xs: 12 }}>
                <Alert severity="info" sx={{ mb: 1 }}>
                    {t('integrations.expedia.info', {
                        defaultValue:
                            'Configure your Expedia Local Expert supplier branch. The Supplier Branch ID and Auth Secret are provided by Expedia during onboarding. Map your show using the activity and offer IDs below.',
                    })}
                </Alert>
            </Grid2>
            <Grid2 size={{ xs: 12 }}>
                <TextField
                    fullWidth
                    label={t('integrations.expedia.partnerSupplierBranchId', {
                        defaultValue: 'Partner Supplier Branch ID',
                    })}
                    value={values.settings?.partnerSupplierBranchId || ''}
                    onChange={(ev) =>
                        setFieldValue('settings', {
                            ...values.settings,
                            partnerSupplierBranchId: ev.target.value,
                        })
                    }
                    placeholder="e.g. BRANCH-123"
                    helperText={t('integrations.expedia.partnerSupplierBranchIdHelp', {
                        defaultValue: 'Provided by Expedia. Used to identify your supplier branch.',
                    })}
                />
            </Grid2>
            <Grid2 size={{ xs: 12 }}>
                <TextField
                    fullWidth
                    label={t('integrations.expedia.authSecret', {
                        defaultValue: 'Auth Secret (HMAC Key)',
                    })}
                    value={values.settings?.authSecret || ''}
                    onChange={(ev) =>
                        setFieldValue('settings', {
                            ...values.settings,
                            authSecret: ev.target.value,
                        })
                    }
                    placeholder="Enter your HMAC authentication secret"
                    type="password"
                    helperText={t('integrations.expedia.authSecretHelp', {
                        defaultValue:
                            'Shared secret used to validate incoming requests via HMAC-SHA256.',
                    })}
                />
            </Grid2>
            <Grid2 size={{ xs: 12 }}>
                <FormControl fullWidth>
                    <InputLabel>
                        {t('integrations.columns.affiliateAgent', {
                            defaultValue: 'Affiliate agent',
                        })}
                    </InputLabel>
                    <Select
                        label={t('integrations.columns.affiliateAgent', {
                            defaultValue: 'Affiliate agent',
                        })}
                        value={values.settings?.agent_user_id || ''}
                        onChange={(ev) =>
                            setFieldValue('settings', {
                                ...values.settings,
                                agent_user_id: ev.target.value || null,
                            })
                        }
                    >
                        <MenuItem value="">{t('common.none')}</MenuItem>
                        {agents.map((a) => (
                            <MenuItem key={a.id} value={a.id}>
                                {a.name}
                                {a.commissionGroupNames?.length ? (
                                    <Typography
                                        component="span"
                                        variant="body2"
                                        sx={{ ml: 1, color: 'text.secondary' }}
                                    >
                                        · {a.commissionGroupNames.join(', ')}
                                    </Typography>
                                ) : null}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid2>
            <Grid2 size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('integrations.expedia.mappingTitle', {
                        defaultValue: 'Product Mapping',
                    })}
                </Typography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }}>
                <TextField
                    fullWidth
                    label={t('integrations.expedia.partnerActivityId', {
                        defaultValue: 'Partner Activity ID (Show ID)',
                    })}
                    value={values.showId || ''}
                    disabled
                    helperText={t('integrations.expedia.partnerActivityIdHelp', {
                        defaultValue: 'Use this Show ID as partnerActivityId when configuring AVT.',
                    })}
                />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }}>
                <TextField
                    fullWidth
                    label={t('integrations.expedia.baseUrl', {
                        defaultValue: 'Supplier API Base URL',
                    })}
                    value={`${window.location.origin}/1.0/integrations/expedia`}
                    disabled
                    helperText={t('integrations.expedia.baseUrlHelp', {
                        defaultValue: 'Enter this as the base URL in the AVT configuration.',
                    })}
                />
            </Grid2>
        </>
    );
}

export default ExpediaForm;
